import CompanyLogo from 'components/molecules/CompanyLogo/CompanyLogo';
import Container from 'components/molecules/Container/Container';
import { appSwitchRoutes, getRoleHomeUrl } from 'constants/routes';
import { StageCandidateActionType } from 'enums/stageAction';
import {
  MyRoleStageActionsQuery,
  StageCandidateStatus,
  StageStatus,
  useMyRoleStageActionsQuery,
  useStageCandidateSubmitAdjustmentQuestionnaireMutation,
} from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader,
  NavLink,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from '../AccessibilityAdjustments/AccessibilityAdjustments.module.scss';
import AccessibilityAdjustmentStep from '../AccessibilityAdjustments/AccessibilityAdjustmentStep';
import {
  adjustmentsResponsesType,
  steps,
  stepsOrder,
  TAdjustmentsFlowRoutes,
} from '../AccessibilityAdjustments/AccessibilityAdjustments.helpers';
import { convertExtraTimeValue } from '../Test/Tests/helpers/extraTime';
import { getPortalBrandingStyle } from '../CandidatePortal/CandidatePortal.helpers';

type TMyRoleStage = NonNullable<
  MyRoleStageActionsQuery['myRole']['stages'][number]
>;

/** Flattens the grouped actions object into the legacy action-type list the
 * adjustments steps reason about (which adjustments apply to which tests). */
const toActionTypes = (
  actions: TMyRoleStage['actions']
): StageCandidateActionType[] => [
  ...(actions.personalityTestAction
    ? [StageCandidateActionType.PersonalityTest]
    : []),
  ...actions.cognitiveTestActions.map(
    cognitiveTestAction =>
      ({
        INDUCTIVE: StageCandidateActionType.CognitiveInductiveTest,
        NUMERICAL: StageCandidateActionType.CognitiveNumericalTest,
        VERBAL: StageCandidateActionType.CognitiveVerablTest,
      })[cognitiveTestAction.kind]
  ),
  ...(actions.coderbyteAction
    ? [StageCandidateActionType.CodingExternalAssessment]
    : []),
  ...(actions.englishProficiencyAction
    ? [StageCandidateActionType.EnglishProficiencyTest]
    : []),
  ...(actions.softRssAssessmentAction || actions.technicalRssAssessmentAction
    ? [StageCandidateActionType.RssAssessment]
    : []),
  ...(actions.deiAction ? [StageCandidateActionType.DeiForm] : []),
  ...actions.formActions.map(() => StageCandidateActionType.Form),
  ...actions.aiInterviewActions.map(
    () => StageCandidateActionType.AiInterviews
  ),
];

const RoleStageAdjustments = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const { roleId, stageId, step } = useParams();

  const [responses, setResponses] = useState<adjustmentsResponsesType>([]);
  const [completedStep, setCompletedStep] = useState<steps>('');
  // Decided by the backend from the responses it has just stored, so the
  // details step can only ask for it once the questionnaire is submitted.
  const [shouldContactUs, setShouldContactUs] = useState(false);

  const roleStagesUrl = appSwitchRoutes.role.getUrl(roleId as string);
  const actionsUrl = appSwitchRoutes.roleStageActions.getUrl(
    roleId as string,
    stageId as string
  );

  const { data, loading } = useMyRoleStageActionsQuery({
    variables: {
      roleId: roleId as string,
      stageId: stageId as string,
    },
    skip: !roleId || !stageId,
    onError: error => {
      // Backend identity-verification gate (covers deep links into the
      // adjustments flow): divert into the verification flow, which returns
      // to the stage actions page once approved.
      const needsIdVerification = error.graphQLErrors?.some(gqlError =>
        gqlError.message.includes('IDENTITY_VERIFICATION_REQUIRED')
      );
      if (needsIdVerification) {
        navigate(
          appSwitchRoutes.roleStageIdentityVerification.getUrl(
            roleId as string,
            stageId as string
          )
        );
        return;
      }

      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });

      navigate(roleStagesUrl);
    },
    onCompleted: queryData => {
      const homeUrl = getRoleHomeUrl(
        roleId as string,
        queryData.myRole.candidatePortalEnabled
      );
      const stage = queryData.myRole.stages[0];
      if (!stage) {
        navigate(homeUrl);
        return;
      }

      // Redirect to the actions page if the stage has no questionnaire.
      if (!stage.hasAdjustmentQuestionnaire) {
        navigate(actionsUrl);
      }

      if (
        stage.stageStatus === StageStatus.Expired ||
        stage.stageStatus === StageStatus.Scheduled ||
        stage.candidateStatus === StageCandidateStatus.Completed ||
        stage.candidateStatus === StageCandidateStatus.SignedOff ||
        stage.candidateStatus === StageCandidateStatus.ManagerCompleted ||
        stage.hasCompletedAdjustmentQuestionnaire
      ) {
        navigate(homeUrl);
      }
    },
    fetchPolicy: 'network-only',
  });

  const backUrl = getRoleHomeUrl(
    roleId as string,
    data?.myRole?.candidatePortalEnabled
  );

  useEffect(() => {
    const completedStepIndex = stepsOrder.indexOf(completedStep);
    const currentStepIndex = stepsOrder.indexOf((step as steps) || '');
    // Block user from navigating backwards with browser button after submitting the form
    if (completedStep === 'details' && currentStepIndex < completedStepIndex) {
      navigate(backUrl);
    }
  }, [step, navigate, completedStep, backUrl]);

  const [submitQuestionnaire, { loading: submitLoading }] =
    useStageCandidateSubmitAdjustmentQuestionnaireMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
    });

  const role = data?.myRole;
  const stage = role?.stages[0];

  const flowRoutes: TAdjustmentsFlowRoutes = useMemo(
    () => ({
      getStepUrl: flowStep =>
        appSwitchRoutes.roleStageAdjustments.getUrl(
          roleId as string,
          stageId as string,
          flowStep
        ),
      exitUrl: actionsUrl,
    }),
    [roleId, stageId, actionsUrl]
  );

  const tests = useMemo(
    () => (stage ? toActionTypes(stage.actions) : []),
    [stage]
  );

  // Pre-existing extra-time modifiers per action, shown on the details step
  // instead of the newly selected extra time.
  const extraTimeOverrides = useMemo(() => {
    const overrides: Partial<Record<StageCandidateActionType, string>> = {};
    stage?.actions.cognitiveTestActions.forEach(cognitiveTestAction => {
      if (cognitiveTestAction.timeModifierPercentage) {
        overrides[
          {
            INDUCTIVE: StageCandidateActionType.CognitiveInductiveTest,
            NUMERICAL: StageCandidateActionType.CognitiveNumericalTest,
            VERBAL: StageCandidateActionType.CognitiveVerablTest,
          }[cognitiveTestAction.kind]
        ] = `+${convertExtraTimeValue(
          cognitiveTestAction.timeModifierPercentage
        )}%`;
      }
    });
    if (stage?.actions.coderbyteAction?.timeModifierPercentage) {
      overrides[StageCandidateActionType.CodingExternalAssessment] =
        `+${convertExtraTimeValue(
          stage.actions.coderbyteAction.timeModifierPercentage
        )}%`;
    }
    return overrides;
  }, [stage]);

  const handleResponses = (values: adjustmentsResponsesType, step: steps) => {
    setResponses(values);
    handleProgress(step);
  };

  const handleProgress = (step: steps) => {
    setCompletedStep(step);
  };

  const handleSubmitResponses = (redirectUrl: string, step?: steps) => {
    if (step) {
      handleProgress(step);
    }
    submitQuestionnaire({
      variables: {
        responses: responses,
        stageCandidateId: stage?.stageCandidateId as number,
      },
      onCompleted: data => {
        setShouldContactUs(
          data.StageCandidateSubmitAdjustmentQuestionnaire.shouldContactUs
        );
        navigate(redirectUrl);
      },
    });
    return;
  };

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (!role || !stage) {
    return null;
  }

  return (
    <Container
      className={styles.container}
      dataTestId="accessibility-adjustment"
      // Whitelabel colours: primary buttons in the flow pick these up via
      // the design-system button's --button-primary-bg* hooks.
      style={getPortalBrandingStyle(role.company.branding)}
    >
      <header>
        <nav className="navbar">
          <NavLink
            to={backUrl}
            onClick={e => {
              e.preventDefault();
              navigate(backUrl);
            }}
            aria-label={t('roles.backToStageList')}
            direction="backward"
          >
            {t('roles.stageList')}
          </NavLink>
        </nav>
      </header>
      <main className={styles.content} aria-live="polite">
        <div className={styles.details}>
          <CompanyLogo
            companyLogoUrl={
              role.company.branding?.logoUrl || role.company.logoUrl
            }
            companyName={role.company.name}
          />
          <h1 className={styles.title} tabIndex={-1}>
            {role.name}
          </h1>
        </div>
        <h2 className={styles.sectionTitle}>
          {t('accessibilityAdjustments.title')}
        </h2>
        <AccessibilityAdjustmentStep
          completedStep={completedStep}
          step={step}
          handleProgress={handleProgress}
          handleResponses={handleResponses}
          handleSubmitResponses={handleSubmitResponses}
          responses={responses}
          loading={submitLoading}
          tests={tests}
          flowRoutes={flowRoutes}
          companyName={role.company.name}
          companyReasonableAdjustmentsOption={
            role.company.reasonableAdjustmentsOption ?? undefined
          }
          usesBehaviouralLabel={role.company.usesBehaviouralLabel}
          extraTimeOverrides={extraTimeOverrides}
          shouldContactUs={shouldContactUs}
          isLiveChatEnabled={role.company.isLiveChatEnabled}
        />
      </main>
    </Container>
  );
};

export default RoleStageAdjustments;
